import React from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import noop from 'lodash/noop';

import { ConnectedProps } from 'mf-shared/types/common';

import PasswordField from './PasswordField';
import AccountAssociationModal from './AccountAssociationModal';

import styles from './GoAuth.css';

function mapStateToProps(state) {
  const { isNewlyAssociated } = state.login;
  return {
    isNewlyAssociated,
  };
}

const connector = connect(mapStateToProps);

interface GoAuthProps {
  readonly loginId: string;
  readonly isNewlyAssociated: boolean;
  readonly appName: string;
  readonly onReset: () => void;
  readonly onNext: (password: string) => void;
}

interface GoAuthState {
  readonly password: string;
  readonly errorMessage: string;
  readonly associationModalManualOpen: boolean;
  readonly associationModalClosed: boolean;
}

type Props = ConnectedProps<typeof connector, GoAuthProps>;

class GoAuth extends React.Component<Props, GoAuthState> {
  static defaultProps = {
    loginId: '',
    isNewlyAssociated: false,
    appName: '',
    onReset: noop,
  };

  state = {
    password: '',
    errorMessage: '',
    associationModalManualOpen: false,
    associationModalClosed: false,
  };

  handlePasswordChange = (e) => {
    this.setState({
      password: e.target.value,
    });
  };

  handleSubmit = (e) => {
    e.preventDefault();

    const { onNext } = this.props;
    const { password } = this.state;
    const error = this.validateForm();

    if (!error) {
      onNext(password);
    } else {
      this.setState({
        errorMessage: error,
      });
    }
  };

  validateForm = () => {
    const { password } = this.state;

    if (!password) {
      return 'Enter a password';
    }

    if (password.length < 6) {
      return 'Password must be 6 or more characters';
    }

    return '';
  };

  showAssociationModalManually = () => {
    this.setState({
      associationModalManualOpen: true,
    });
  };

  handleAssociationModalClose = () => {
    this.setState({
      associationModalManualOpen: false,
      associationModalClosed: true,
    });
  };

  render() {
    const { loginId, isNewlyAssociated, onReset, appName } = this.props;
    const {
      password,
      errorMessage,
      associationModalClosed,
      associationModalManualOpen,
    } = this.state;

    const isSubmitDisabled = !password;
    const displayAssociationModal =
      associationModalManualOpen ||
      (isNewlyAssociated && !associationModalClosed);

    if (displayAssociationModal) {
      return (
        <AccountAssociationModal onClose={this.handleAssociationModalClose} />
      );
    }

    return (
      <div>
        <div className={classNames('text-info', styles.infoBar)}>
          <div className="container">
            <i className="glyphicon glyphicon-info-sign" />
            &nbsp;Your account has been associated with GoBiz. &nbsp;
            <a onClick={this.showAssociationModalManually}>
              <b>Learn More</b>
            </a>
          </div>
        </div>
        <div className={styles.formContainer}>
          <div className={styles.header}>
            {appName ? (
              <div className={styles.titleOpenPlatform}>
                Sign in to use <strong>{appName}</strong>
              </div>
            ) : (
              <div className={styles.title}>Sign In</div>
            )}

            <div className={styles.loginIdPreview}>
              <span className={styles.loginIdPreviewText}>{loginId}</span>
              <a className={styles.loginIdPreviewChangeLink} onClick={onReset}>
                Change
              </a>
            </div>

            <div className={styles.instruction}>Enter your GoBiz password</div>
          </div>

          <form
            id="password-input-form"
            name="password-input-form"
            onSubmit={this.handleSubmit}
          >
            <div
              className={classNames('form-group', {
                'has-error': errorMessage,
              })}
            >
              <PasswordField
                name="password"
                id="auth-password-input"
                className="form-control"
                placeholder={'GoBiz Password'}
                maxLength="40"
                autoFocus
                value={password}
                onChange={this.handlePasswordChange}
              />
              {errorMessage && (
                <span className="text-danger">{errorMessage}</span>
              )}
            </div>

            <div className="form-group">
              <button
                type="submit"
                id="verify-password-button"
                name="verify-password-button"
                className="btn btn-primary btn-block"
                disabled={isSubmitDisabled}
              >
                Sign In
              </button>
            </div>
          </form>

          <div className={styles.footer}>
            <Link to="/reset-gobiz-password">
              Don't know your GoBiz password?
            </Link>
          </div>
        </div>
      </div>
    );
  }
}

export default connector(GoAuth);
